export type SatonomousDiscoveryScore = 'ready' | 'partial' | 'blocked';
export type SatonomousDiscoveryCheckStatus = 'pass' | 'warn' | 'fail';

export interface SatonomousDiscoveryFetchEvidence {
  url: string;
  status?: number;
  content_type?: string | null;
  user_agent: string;
  body_sample?: string;
}

export interface SatonomousDiscoveryCheck {
  code: string;
  status: SatonomousDiscoveryCheckStatus;
  message: string;
  url?: string;
  evidence?: unknown;
}

export interface SatonomousDiscoveryReport {
  valid: boolean;
  score: SatonomousDiscoveryScore;
  manifest_url: string;
  checked_at: string;
  checks: SatonomousDiscoveryCheck[];
  warnings: string[];
  errors: string[];
}

export interface VerifySatonomousDiscoveryOptions {
  /** User-Agent sent by the verifier. Defaults to an explicit SDK verifier UA. */
  userAgent?: string;
  /** Optional fetch override for tests, workers, or custom runtimes. */
  fetch?: typeof fetch;
  /** AbortSignal forwarded to verifier fetches. */
  signal?: AbortSignal;
  /** Skip OpenAPI fetch/content checks and only validate the discovery manifest. */
  skipOpenApi?: boolean;
  /** Timestamp override for deterministic tests. */
  checkedAt?: string;
}

const DEFAULT_VERIFIER_UA = 'satonomous-discovery-verifier/0.1 (+https://github.com/jordiagi/satonomous)';

function normalizeManifestUrl(input: string): string {
  const url = new URL(input);
  if (url.pathname.endsWith('/.well-known/satonomous.json')) return url.toString();
  url.pathname = '/.well-known/satonomous.json';
  url.search = '';
  url.hash = '';
  return url.toString();
}

function headerValue(headers: Headers, name: string): string | null {
  try {
    return headers.get(name);
  } catch {
    return null;
  }
}

function bodySample(text: string): string {
  return text.replace(/\s+/g, ' ').trim().slice(0, 240);
}

function isRecord(value: unknown): value is Record<string, any> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function add(
  checks: SatonomousDiscoveryCheck[],
  status: SatonomousDiscoveryCheckStatus,
  code: string,
  message: string,
  url?: string,
  evidence?: unknown
): void {
  checks.push({ code, status, message, url, evidence });
}

function statusFromChecks(checks: SatonomousDiscoveryCheck[]): SatonomousDiscoveryScore {
  if (checks.some((check) => check.status === 'fail')) return 'blocked';
  if (checks.some((check) => check.status === 'warn')) return 'partial';
  return 'ready';
}

async function readText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return '';
  }
}

async function fetchText(
  fetchImpl: typeof fetch,
  url: string,
  userAgent: string,
  signal?: AbortSignal
): Promise<{ ok: true; text: string; evidence: SatonomousDiscoveryFetchEvidence } | { ok: false; code: string; evidence: SatonomousDiscoveryFetchEvidence }> {
  let response: Response;
  try {
    response = await fetchImpl(url, {
      signal,
      headers: {
        'Accept': 'application/json, text/plain;q=0.8, */*;q=0.1',
        'User-Agent': userAgent,
      },
    });
  } catch (err: any) {
    return {
      ok: false,
      code: 'manifest_unreachable',
      evidence: {
        url,
        user_agent: userAgent,
        body_sample: err?.message ? String(err.message).slice(0, 240) : String(err).slice(0, 240),
      },
    };
  }

  const text = await readText(response);
  const evidence: SatonomousDiscoveryFetchEvidence = {
    url,
    status: response.status,
    content_type: headerValue(response.headers, 'content-type'),
    user_agent: userAgent,
    body_sample: bodySample(text),
  };

  if (!response.ok) {
    const sample = evidence.body_sample?.toLowerCase() ?? '';
    const code = response.status === 403 && sample.includes('1010')
      ? 'edge_bot_rule_blocked'
      : 'manifest_unreachable';
    return { ok: false, code, evidence };
  }

  return { ok: true, text, evidence };
}

function findOpenApiUrl(manifest: Record<string, any>): string | undefined {
  const candidates = [
    manifest.openapi,
    manifest.openapi_url,
    manifest.gateway?.openapi,
    manifest.gateway?.openapi_url,
    manifest.developer?.gateway?.openapi,
    manifest.developer?.gateway?.openapi_url,
    manifest.links?.openapi,
  ];
  return candidates.find((value) => typeof value === 'string' && /^https?:\/\//.test(value));
}

function assertManifestShape(manifest: Record<string, any>, checks: SatonomousDiscoveryCheck[], manifestUrl: string): void {
  const primitives = isRecord(manifest.primitives) ? manifest.primitives : {};
  const feedback = isRecord(manifest.feedback) ? manifest.feedback : {};

  for (const primitive of ['service_cards', 'work_orders', 'contract_receipts']) {
    add(
      checks,
      primitives[primitive] ? 'pass' : 'fail',
      primitives[primitive] ? `${primitive}_present` : 'required_primitive_missing',
      primitives[primitive] ? `Discovery advertises ${primitive}.` : `Discovery is missing required primitive ${primitive}.`,
      manifestUrl
    );
  }

  const hasWatcherPrimitive = Boolean(primitives.watchers || primitives.contract_event_loop);
  add(
    checks,
    hasWatcherPrimitive ? 'pass' : 'warn',
    hasWatcherPrimitive ? 'watcher_primitive_present' : 'watcher_primitive_missing',
    hasWatcherPrimitive
      ? 'Discovery advertises watcher/event-loop readiness.'
      : 'Discovery does not advertise watcher/event-loop readiness.',
    manifestUrl
  );

  const hasSecurity = isRecord(manifest.security) && Object.keys(manifest.security).length > 0;
  add(
    checks,
    hasSecurity ? 'pass' : 'warn',
    hasSecurity ? 'security_metadata_present' : 'security_metadata_missing',
    hasSecurity ? 'Security metadata is present.' : 'Security metadata is missing or empty.',
    manifestUrl
  );

  const hasFeedback = Boolean(feedback.issues || feedback.contract_primitives || feedback.integration_friction);
  add(
    checks,
    hasFeedback ? 'pass' : 'warn',
    hasFeedback ? 'feedback_path_present' : 'feedback_path_missing',
    hasFeedback ? 'Discovery exposes at least one feedback route.' : 'Discovery does not expose a usable feedback path.',
    manifestUrl,
    feedback
  );
}

async function assertOpenApi(
  manifest: Record<string, any>,
  checks: SatonomousDiscoveryCheck[],
  fetchImpl: typeof fetch,
  userAgent: string,
  signal?: AbortSignal
): Promise<void> {
  const openApiUrl = findOpenApiUrl(manifest);
  if (!openApiUrl) {
    add(checks, 'warn', 'openapi_url_missing', 'Discovery does not expose a gateway OpenAPI URL.');
    return;
  }

  const fetched = await fetchText(fetchImpl, openApiUrl, userAgent, signal);
  if (!fetched.ok) {
    add(checks, fetched.code === 'edge_bot_rule_blocked' ? 'fail' : 'warn', fetched.code === 'edge_bot_rule_blocked' ? 'programmatic_fetch_blocked' : 'openapi_unreachable', 'OpenAPI fetch failed during programmatic verifier preflight.', openApiUrl, fetched.evidence);
    return;
  }

  let openapi: any;
  try {
    openapi = JSON.parse(fetched.text);
  } catch {
    add(checks, 'warn', 'openapi_not_json', 'OpenAPI URL resolved but did not parse as JSON.', openApiUrl, fetched.evidence);
    return;
  }

  add(checks, 'pass', 'openapi_json_ok', 'OpenAPI resolves and parses as JSON.', openApiUrl, {
    ...fetched.evidence,
    openapi: openapi.openapi,
  });

  const paths = isRecord(openapi.paths) ? openapi.paths : {};
  const hasContractEvents = Boolean(paths['/api/v1/contracts/{contract_id}/events']);
  const hasDepositEvents = Boolean(paths['/api/v1/wallet/deposit/{payment_hash}/events']);
  add(
    checks,
    hasContractEvents && hasDepositEvents ? 'pass' : 'warn',
    hasContractEvents && hasDepositEvents ? 'openapi_event_paths_present' : 'openapi_event_paths_missing',
    hasContractEvents && hasDepositEvents
      ? 'OpenAPI advertises contract and deposit event paths.'
      : 'OpenAPI is missing one or more event/watch paths.',
    openApiUrl,
    { hasContractEvents, hasDepositEvents }
  );
}

export async function verifySatonomousDiscovery(
  inputUrl: string,
  options: VerifySatonomousDiscoveryOptions = {}
): Promise<SatonomousDiscoveryReport> {
  const manifest_url = normalizeManifestUrl(inputUrl);
  const checked_at = options.checkedAt ?? new Date().toISOString();
  const checks: SatonomousDiscoveryCheck[] = [];
  const fetchImpl = options.fetch ?? globalThis.fetch;
  const userAgent = options.userAgent ?? DEFAULT_VERIFIER_UA;

  if (!fetchImpl) {
    add(checks, 'fail', 'fetch_unavailable', 'No fetch implementation is available in this runtime.', manifest_url);
    const score = statusFromChecks(checks);
    return { valid: false, score, manifest_url, checked_at, checks, warnings: [], errors: ['fetch_unavailable'] };
  }

  const fetched = await fetchText(fetchImpl, manifest_url, userAgent, options.signal);
  if (!fetched.ok) {
    add(checks, fetched.code === 'edge_bot_rule_blocked' ? 'fail' : 'fail', fetched.code === 'edge_bot_rule_blocked' ? 'programmatic_fetch_blocked' : fetched.code, 'Discovery manifest fetch failed during programmatic verifier preflight.', manifest_url, fetched.evidence);
    const score = statusFromChecks(checks);
    return {
      valid: false,
      score,
      manifest_url,
      checked_at,
      checks,
      warnings: checks.filter((check) => check.status === 'warn').map((check) => check.code),
      errors: checks.filter((check) => check.status === 'fail').map((check) => check.code),
    };
  }

  let manifest: any;
  try {
    manifest = JSON.parse(fetched.text);
  } catch {
    add(checks, 'fail', 'manifest_not_json', 'Discovery manifest resolved but did not parse as JSON.', manifest_url, fetched.evidence);
    const score = statusFromChecks(checks);
    return { valid: false, score, manifest_url, checked_at, checks, warnings: [], errors: ['manifest_not_json'] };
  }

  if (!isRecord(manifest)) {
    add(checks, 'fail', 'manifest_shape_invalid', 'Discovery manifest parsed but was not a JSON object.', manifest_url, fetched.evidence);
  } else {
    add(checks, 'pass', 'manifest_json_ok', 'Discovery manifest resolves and parses as JSON.', manifest_url, fetched.evidence);
    assertManifestShape(manifest, checks, manifest_url);
    if (!options.skipOpenApi) await assertOpenApi(manifest, checks, fetchImpl, userAgent, options.signal);
  }

  const score = statusFromChecks(checks);
  return {
    valid: score !== 'blocked',
    score,
    manifest_url,
    checked_at,
    checks,
    warnings: checks.filter((check) => check.status === 'warn').map((check) => check.code),
    errors: checks.filter((check) => check.status === 'fail').map((check) => check.code),
  };
}
